import React, { useState } from 'react';
import { ArrowLeft, Container, Archive, Terminal, LoaderCircle, Rocket, Info, Copy, Check } from 'lucide-react';
import { generateRandomSuffix, validateSubdomainPrefix } from '../../utils/subdomainHelper';

export default function DockerImageDeploy({ 
  user, onStatusMessage, onBack, api, 
  beginDeploymentTracking, refreshDeploymentSession, setCurrentSessionProgress,
  onTabChange
}) {
  const [dockerAppName, setDockerAppName] = useState('');
  const [dockerAppNameError, setDockerAppNameError] = useState('');
  const [platformSuffix] = useState(() => generateRandomSuffix());
  const [subdomainPrefix, setSubdomainPrefix] = useState('');
  const [subdomainError, setSubdomainError] = useState('');
  const [dockerDomain, setDockerDomain] = useState('');
  const [dockerDomainError, setDockerDomainError] = useState('');
  const [internalPort, setInternalPort] = useState('80');

  const handleSubdomainChange = (e) => {
    const val = e.target.value;
    setSubdomainPrefix(val);
    const res = validateSubdomainPrefix(val);
    setSubdomainError(res.valid ? '' : res.error);
  };

  const validateDockerDomain = (val) => {
    if (!val || !val.trim()) {
      setDockerDomainError('');
      return true;
    }
    if (/devorants\.com$/i.test(val.trim())) {
      setDockerDomainError('devorants.com subdomains cannot be used as custom domains.');
      return false;
    }
    setDockerDomainError('');
    return true;
  };

  const handleDockerDomainChange = (e) => {
    const val = e.target.value;
    setDockerDomain(val);
    validateDockerDomain(val);
  };
  const [selectedDockerPort, setSelectedDockerPort] = useState('');
  const [dockerFile, setDockerFile] = useState(null);
  const [tarCommandCopied, setTarCommandCopied] = useState(false);
  const [category, setCategory] = useState('backend');
  const [cpus, setCpus] = useState('1');
  const [memory, setMemory] = useState('512m');
  const [loading, setLoading] = useState(false);

  const handleCopyTarCommand = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const cmd = 'docker save -o my-image.tar <image-name>:<tag>';
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(cmd);
      setTarCommandCopied(true);
      setTimeout(() => setTarCommandCopied(false), 2000);
    }
  };

  const validateDockerAppName = (val) => {
    if (!val) {
      setDockerAppNameError('');
      return false;
    }
    if (/\s/.test(val)) {
      setDockerAppNameError('Spaces are not allowed. Use hyphens (-) instead.');
      return false;
    }
    if (/[A-Z]/.test(val)) {
      setDockerAppNameError('Uppercase letters are not allowed. Use lowercase only.');
      return false;
    }
    if (!/^[a-z0-9-]+$/.test(val)) {
      setDockerAppNameError('Only lowercase letters, numbers, and hyphens (-) are allowed.');
      return false;
    }
    setDockerAppNameError('');
    return true;
  };

  const handleDockerAppNameChange = (e) => {
    const val = e.target.value;
    setDockerAppName(val);
    validateDockerAppName(val);
  };

  const handlePortChange = (value) => {
    setSelectedDockerPort(value);
  };

  const handleDockerDeploy = async (e) => {
    e.preventDefault();
    if (!dockerAppName || !validateDockerAppName(dockerAppName) || !dockerFile || !internalPort) {
      onStatusMessage(dockerAppNameError || 'Please fill in all fields properly', 'error');
      return;
    }

    if (subdomainPrefix && !validateSubdomainPrefix(subdomainPrefix).valid) {
      onStatusMessage(subdomainError || 'Invalid subdomain format.', 'error');
      return;
    }

    if (dockerDomain && !validateDockerDomain(dockerDomain)) {
      onStatusMessage(dockerDomainError || 'devorants.com subdomains cannot be used as custom domains.', 'error');
      return;
    }

    const resolvedPrefix = (subdomainPrefix && subdomainPrefix.trim())
      ? subdomainPrefix.trim().toLowerCase()
      : (dockerAppName || 'app').trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
    const autoPlatformDomain = `${resolvedPrefix}-${platformSuffix}.devorants.com`;
    const finalDomain = (dockerDomain && dockerDomain.trim()) ? dockerDomain.trim() : autoPlatformDomain;

    setLoading(true);
    const deploymentId = beginDeploymentTracking('docker', dockerAppName);

    try {
      api.uploadDocker(dockerAppName, internalPort, category, dockerFile, cpus, memory, {
        deploymentId,
        domain: finalDomain,
        publicPort: selectedDockerPort ? parseInt(selectedDockerPort) : undefined,
        volumes: undefined,
        onUploadProgress: setCurrentSessionProgress
      }).then(
        (data) => {
          if (data && data.cancelled) return;
          setDockerAppName('');
          setSubdomainPrefix('');
          setSubdomainError('');
          setDockerDomain('');
          setDockerDomainError('');
          setDockerFile(null);
          // clearFileInputs logic
          const fileInput = document.getElementById('dockerInput');
          if (fileInput) fileInput.value = '';
          refreshDeploymentSession(deploymentId);
          onStatusMessage(data?.message || 'Docker container deployed successfully!', 'success');
          // Navigate to dashboard after a short delay so progress tracking can finish
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('switch-to-dashboard'));
            if (onTabChange) onTabChange('deployments');
          }, 1500);
        },
        (err) => {
          onStatusMessage(`Error: ${err.message}`, 'error');
          refreshDeploymentSession(deploymentId);
        }
      ).finally(() => {
        setLoading(false);
        setCurrentSessionProgress(100);
      });
    } catch (err) {
      onStatusMessage(`Error: ${err.message}`, 'error');
      setLoading(false);
    }
  };

  return (
    <div className="vercel-form-body">
      <button type="button" className="vercel-back-btn-new" onClick={onBack}>
        <ArrowLeft size={16} />
        <span>Back to selection</span>
      </button>
      <form onSubmit={handleDockerDeploy} className="vercel-premium-form">
        <div className="form-section-group">
          <div className="form-section-header">
            <Container size={18} />
            <h3>Container Config</h3>
          </div>
          <div className="form-row-grid">
            <div className="form-group">
              <label>Application Name <span className="required-star">*</span></label>
              <input 
                type="text" 
                placeholder="e.g. my-docker-app" 
                required 
                value={dockerAppName} 
                onChange={handleDockerAppNameChange} 
                style={dockerAppNameError ? { borderColor: 'var(--danger, #ef4444)' } : {}}
              />
              {dockerAppNameError ? (
                <p className="form-feedback" style={{ color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                  {dockerAppNameError}
                </p>
              ) : (
                <p className="form-feedback" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  Use lowercase, numbers, and hyphens only.
                </p>
              )}
            </div>
            <div className="form-group">
              <label>Platform Subdomain</label>
              <div className={`platform-subdomain-group ${subdomainError ? 'has-error' : ''}`}>
                <input 
                  type="text" 
                  placeholder={dockerAppName ? dockerAppName.toLowerCase() : "e.g. my-app"} 
                  value={subdomainPrefix} 
                  onChange={handleSubdomainChange}
                />
                <span className="platform-subdomain-suffix">
                  {`-${platformSuffix}.devorants.com`}
                </span>
              </div>
              {subdomainError ? (
                <p className="form-feedback" style={{ color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                  {subdomainError}
                </p>
              ) : (
                <p className="form-feedback" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  Preview: <strong style={{ color: 'var(--primary, #6366f1)' }}>{`${(subdomainPrefix || dockerAppName || 'app').trim().toLowerCase().replace(/[^a-z0-9-]/g, '-')}-${platformSuffix}.devorants.com`}</strong>
                </p>
              )}
            </div>
          </div>
          <div className="form-row-grid">
            <div className="form-group">
              <label>Custom Domain (optional)</label>
              <input 
                type="text" 
                placeholder="e.g. docker.example.com" 
                value={dockerDomain} 
                onChange={handleDockerDomainChange}
                style={dockerDomainError ? { borderColor: 'var(--danger, #ef4444)' } : {}}
              />
              {dockerDomainError ? (
                <p className="form-feedback" style={{ color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                  {dockerDomainError}
                </p>
              ) : (
                <p className="form-feedback" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  Your own registered domain. Leave blank to use devorants.com subdomain.
                </p>
              )}
            </div>
            <div className="form-group">
              <label>Internal Port <span className="required-star">*</span></label>
              <input type="text" placeholder="80" value={internalPort} onChange={(e) => setInternalPort(e.target.value)} />
            </div>
          </div>
          <div className="form-row-grid">
            <div className="form-group">
              <label>Public Port (Optional)</label>
              <input type="number" placeholder="Auto-assign" value={selectedDockerPort} onChange={(e) => handlePortChange(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="form-section-group">
          <div className="form-section-header">
            <Archive size={18} />
            <h3>Image Upload</h3>
          </div>
          <div className="form-group">
            <label>Docker Image (.tar) <span className="required-star">*</span></label>
            <div className="premium-drop-zone" onClick={() => document.getElementById('dockerInput').click()}>
              <Container size={40} className="drop-icon" />
              <div className="drop-text">
                {dockerFile ? <b>{dockerFile.name}</b> : "Select your Docker image tarball"}
              </div>
              <input id="dockerInput" type="file" accept=".tar,.tar.gz,.tgz" onChange={(e) => setDockerFile(e.target.files[0])} style={{ display: 'none' }} />
            </div>

            <div style={{
              marginTop: '0.85rem',
              padding: '0.85rem 1rem',
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              fontSize: '0.82rem',
              color: 'var(--text-main)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, color: 'var(--text-main)', marginBottom: '0.35rem' }}>
                <Info size={15} style={{ color: 'var(--primary)' }} />
                <span>How to create a .tar Docker image?</span>
              </div>
              <p style={{ margin: '0 0 0.5rem 0', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                Export any local Docker image from your terminal using:
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'var(--surface-1, var(--surface))',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                padding: '0.5rem 0.75rem',
                fontFamily: 'monospace',
                fontSize: '0.82rem'
              }}>
                <code style={{ color: 'var(--text-main)', fontWeight: 600 }}>
                  docker save -o my-image.tar &lt;image-name&gt;:&lt;tag&gt;
                </code>
                <button
                  type="button"
                  onClick={handleCopyTarCommand}
                  style={{
                    background: tarCommandCopied ? 'rgba(34, 197, 94, 0.15)' : 'var(--surface-2)',
                    border: `1px solid ${tarCommandCopied ? '#22c55e' : 'var(--border)'}`,
                    borderRadius: '4px',
                    color: tarCommandCopied ? '#16a34a' : 'var(--text-main)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                    padding: '3px 8px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    transition: 'all 0.15s ease'
                  }}
                  title="Copy command"
                >
                  {tarCommandCopied ? <Check size={13} /> : <Copy size={13} />}
                  <span>{tarCommandCopied ? 'Copied' : 'Copy'}</span>
                </button>
              </div>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.76rem', color: 'var(--text-muted)' }}>
                Tip: If your image is already hosted on Docker Hub, use the <strong>Import from Docker Hub</strong> option instead.
              </p>
            </div>
          </div>
        </div>

        <div className="form-section-group">
          <div className="form-section-header">
            <Terminal size={18} />
            <h3>Hardware Resources</h3>
          </div>
          <div className="form-row-grid">
            <div className="form-group">
              <label>Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="frontend">Frontend</option>
                <option value="backend">Backend</option>
              </select>
            </div>
            <div className="form-group" style={{ display: 'flex', gap: '0.75rem' }}>
              <div style={{ flex: 1 }}>
                <label>CPU Limit</label>
                <select value={cpus} onChange={(e) => setCpus(e.target.value)} style={{ width: '100%' }}>
                  <option value="1">1 CPU</option>
                  <option value="2">2 CPUs</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label>Memory</label>
                <select value={memory} onChange={(e) => setMemory(e.target.value)} style={{ width: '100%' }}>
                  <option value="256m">256MB</option>
                  <option value="512m">512MB</option>
                  <option value="1g">1GB</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <button type="submit" disabled={loading || Boolean(dockerAppNameError) || !dockerAppName.trim() || !dockerFile || !internalPort} className="premium-deploy-btn" style={dockerAppNameError ? { opacity: 0.6, cursor: 'not-allowed' } : {}}>
          {loading ? <LoaderCircle size={20} className="deploy-spin" /> : <Rocket size={20} />}
          <span>{loading ? 'Building Container...' : 'Deploy Docker Image'}</span>
        </button>
      </form>
    </div>
  );
}
